import React, { FC, useCallback, useMemo, useState } from 'react';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { Button, Popconfirm, Popover, message } from 'antd';
import { DownloadOutlined, EditOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { EmptyState } from '../empty_state/EmptyState';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { EQUIPMENT_TYPES_PER_STOCK_LOCATION_CRUD_ACTION_CREATORS } from '../../actions/equipment_types_per_stock_location_actions';
import {
	equipmentTypesPerStockLocationCsvReportGenerator,
	equipmentTypesPerStockLocationRestCrudThunksForSupplier,
	fetchAllEquipmentTypesPerStockLocactionForSupplier,
} from '../../thunks/equipment_types_per_stock_location_thunks';
import {
	fetchWithDifferntSearchName,
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
	renderCurrency,
	stopDefaultEvents,
} from '../../utils/DataAccessUtils';
import { EquipmentRowDisplay } from '../equipment_row_display/EquipmentRowDisplay';
import BulkTransferEquipmentTypeModal from './BulkTransferEquipmentTypeModal';
import {
	getRecordsForCompositeTargetCollection,
	getRecordsForTargetCollection,
} from '../../reducers/standard_reducer_utils';
import { QuantityRowDisplay } from '../part_quantity_row_display/PartQuantityRowDisplay';
import FeildTechSLManagePermissionChecker, {
	canFieldManageStockLocation,
} from '../common/FeildTechSLManagePermissionChecker';
import ImportGridModal from '../ow_import_grid/ImportGridModal';
import { EQUIPMENT_IMPORT_HEADER, getRecordForEquipmentLevel } from './stock_location_constants';
import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS, FILTER_CONFIG_NAMES, ROLE_TYPES } from '../../utils/DataConstants';
import moment from 'moment';
import { DATE_FORMAT } from '../../utils/DataFormatterUtils';
import OWImporter from '../ow_import_grid/OWImporter';
import { renderEquipmenTypeSelect } from '../purchase_order_form/ReplaceEquipmentLineItemModal';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { assetTypesRestCrudThunksForSupplier } from '../../thunks/asset_types_thunks';
import { equipmentTypesRestCrudThunksForSupplier } from '../../thunks/equipment_types_thunks';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import AddOrEditEquipmentLevelModal from './AddOrEditEquipmentLevelModal';

const TC_NAME = 'equipmentTypesPerStockLocationIndex';
const EQU_LEVEL_ADVANCED_FILTERS = 'equipmentLevelAdvancedFilters';

const StockLocationsEquipmentLevelPage: FC<any> = ({
	match,
	history,
	updateFilters,
	equipmentTypesPerStockLocation,
	clearAndUpdateFilters,
	fetchEquipmentTypesPerStockLocation,
	currentUser,
	stockLocation,
	companyConfig,
	fetchAllEquipmentTypesPerStockLocation,
	createBulk,
	assetTypes,
	equipmentTypes,
	fetchAssetTypes,
	fetchMultipleAssetTypes,
	fetchEquipmentTypes,
	fetchMultipleEquipments,
	deleteEquipmentLevel,
	exportCsv,
}): React.ReactElement => {
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [transferModalVisible, setTransferModalVisible] = useState(false);

	const stockLocationId = useMemo(() => match.params.id, [match.params.id]);

	const [fetchingEqLevels, setFetchingEqLevels] = useState(false);
	const [canOpenEditEquLevel, setCanOpenEditEquLevel] = useState(false);
	const [equipmentLevels, setEquipmentLevels] = useState([]);

	const [downloading, setDownloading] = useState(false);
	const [exportingCsv, setExportingCsv] = useState(false);
	const [uploadPopupVisible, setUploadPopupVisible] = useState(false);

	const [deletingId, setDeletingId] = useState(null);

	const [editingRecord, setEditingRecord] = useState(null);
	const [epslModalVisible, setEpslModalVisible] = useState(false);

	const editAllowed = useMemo(
		() => getRecordsForTargetCollection(equipmentTypesPerStockLocation, TC_NAME).length > 0,
		[equipmentTypesPerStockLocation]
	);

	const filterConfig = useMemo(
		() => [
			{
				label: 'Equipment Type',
				mode: 'multiple',
				fieldName: FILTER_CONFIG_NAMES.EQUIPMENT_TYPE,
				stateSlice: equipmentTypes,
				targetCollectionName: EQU_LEVEL_ADVANCED_FILTERS,
				fetch: fetchWithDifferntSearchName(fetchEquipmentTypes, 'search'),
				fetchMultiple: fetchMultipleEquipments,
				renderRecord: renderEquipmenTypeSelect,
				component: OWAsyncSelect,
			},
			{
				label: 'Asset Type',
				fieldName: FILTER_CONFIG_NAMES.ASSET_TYPE,
				mode: 'multiple',
				stateSlice: assetTypes,
				targetCollectionName: EQU_LEVEL_ADVANCED_FILTERS,
				fetch: fetchAssetTypes,
				fetchMultiple: fetchMultipleAssetTypes,
			},
			{
				fieldName: 'isThresholdReached',
				type: 'radioGroup',
				items: [
					{
						label: 'All',
						value: 'undefined',
					},
					{
						label: 'Less than min quantity',
						value: 'true',
					},
				],
				defaultValue: undefined,
				targetCollectionName: TC_NAME,
				showOutsideDropdown: true,
			},
			{
				fieldName: 'isOutOfStock',
				type: 'radioGroup',
				items: [
					{
						label: 'All',
						value: 'undefined',
					},
					{
						label: 'Out of stock',
						value: 'true',
					},
				],
				defaultValue: undefined,
				targetCollectionName: TC_NAME,
				showOutsideDropdown: true,
			},
		],
		[
			assetTypes,
			equipmentTypes,
			fetchAssetTypes,
			fetchEquipmentTypes,
			fetchMultipleAssetTypes,
			fetchMultipleEquipments,
		]
	);

	const onQtyClick = useCallback(
		(record) => (e) => {
			stopDefaultEvents(e);
			history.push(
				`/supplier/stockLocations/detail/${stockLocationId}/receivedEquipment?stockLocationId=${stockLocationId}&isDisbursed=false&equipmentTypeIds=${nullSafeGet(
					'equipmentType.id',
					record
				)}`
			);
		},
		[history, stockLocationId]
	);

	const getStateSliceProp = useCallback(
		(propName) => nullSafeGetOrElse(`${TC_NAME}.${propName}`, equipmentTypesPerStockLocation, {}),
		[equipmentTypesPerStockLocation]
	);

	const refreshEquipmentTypes = useCallback(() => {
		fetchEquipmentTypesPerStockLocation(
			{},
			TC_NAME,
			getStateSliceProp('pagination'),
			getStateSliceProp('sorting'),
			getStateSliceProp('filters')
		);
	}, [fetchEquipmentTypesPerStockLocation, getStateSliceProp]);

	const onDelete = useCallback(
		(record) => () => {
			if (!!deletingId) {
				return;
			}
			setDeletingId(record.id);
			deleteEquipmentLevel(record)
				.then(() => {
					refreshEquipmentTypes();
				})
				.catch((err) => message.error(err))
				.finally(() => setDeletingId(null));
		},
		[deleteEquipmentLevel, deletingId, refreshEquipmentTypes]
	);

	const _showEditModal = useCallback(
		(record) => () => {
			setEditingRecord(record);
			setEpslModalVisible(true);
		},
		[]
	);

	const _hideEditModal = useCallback(() => {
		setEditingRecord(null);
		setEpslModalVisible(false);
	}, []);

	const columns = useMemo(
		() => [
			{
				title: 'Equipment Type',
				dataIndex: 'equipmentType',
				render: (_) => <EquipmentRowDisplay equipmentType={_} />,
			},
			{
				title: 'Min / Max',
				render: (_, record) => (
					<div className="flex flex-col">
						<div>{`Min: ${nullSafeGetOrElse('minQuantity', record, '--')}`}</div>
						<div>{`Max: ${nullSafeGetOrElse('maxQuantity', record, '--')}`}</div>
					</div>
				),
			},
			{
				title: 'Total Qty',
				dataIndex: 'onHandQuantity',
				render: (quantity, record) => (
					<QuantityRowDisplay
						min={record.minQuantity}
						max={record.maxQuantity}
						quantity={quantity}
						onClick={quantity ? onQtyClick(record) : undefined}
					/>
				),
			},
			{
				title: 'Requested Qty',
				render: () => 0,
			},
			{
				title: 'Available Qty',
				dataIndex: 'onHandQuantity',
			},
			{
				title: 'Total Cost',
				dataIndex: 'totalStockedEquipmentCost',
				render: renderCurrency(currentUser),
			},
			{
				dataIndex: 'onHandQuantity',
				render: (qty, record) => (
					<FeildTechSLManagePermissionChecker>
						<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS}>
							<div className="flex flex-row items-center">
								<Button className="mr-2" type="ghost" onClick={_showEditModal(record)}>
									Edit
								</Button>
								<Popover
									content={
										qty > 0 ? `Equipment type cannot be delisted as it has valid quantity!` : ''
									}
									trigger="hover"
								>
									<Popconfirm
										title="Are you sure you want to delist?"
										onConfirm={onDelete(record)}
										okText="Delist"
										cancelText="Cancel"
										disabled={qty > 0}
									>
										<Button disabled={qty > 0} loading={deletingId === record.id}>
											Delist
										</Button>
									</Popconfirm>
								</Popover>
							</div>
						</AccessPermissionChecker>
					</FeildTechSLManagePermissionChecker>
				),
			},
		],
		[currentUser, deletingId, onDelete, onQtyClick, _showEditModal]
	);

	const onAddEquipment = useCallback(() => {
		setEditingRecord(null);
		setEpslModalVisible(true);
	}, []);

	const onEquipmentLevelSuccess = useCallback(() => {
		_hideEditModal();
		refreshEquipmentTypes();
	}, [_hideEditModal, refreshEquipmentTypes]);

	const canTransfer = useMemo(() => selectedRowKeys.length > 0, [selectedRowKeys.length]);

	const onSelectChange = useCallback((newSelectedRowKeys: React.Key[]) => {
		setSelectedRowKeys(newSelectedRowKeys);
	}, []);

	const rowSelection = useMemo(
		() => ({
			selectedRowKeys,
			hideSelectAll: true,
			preserveSelectedRowKeys: true,
			onChange: onSelectChange,
			getCheckboxProps: (record) => ({
				disabled: !record.onHandQuantity,
			}),
		}),
		[onSelectChange, selectedRowKeys]
	);

	const onTransferClick = useCallback(() => setTransferModalVisible(true), []);

	const hideTransferModal = useCallback(() => setTransferModalVisible(false), []);

	const onSelectCancel = useCallback(() => setSelectedRowKeys([]), []);

	const selectedRecords = useMemo(() => {
		const records = getRecordsForCompositeTargetCollection(equipmentTypesPerStockLocation, TC_NAME);
		return records.filter((_) => selectedRowKeys.includes(_.id));
	}, [equipmentTypesPerStockLocation, selectedRowKeys]);

	const onTransferEquipmentsSuccess = useCallback(() => {
		hideTransferModal();
		onSelectCancel();
		refreshEquipmentTypes();
	}, [hideTransferModal, onSelectCancel, refreshEquipmentTypes]);

	const fetchCurrentSLEntities = useCallback(
		() => fetchAllEquipmentTypesPerStockLocation({ stockLocationId }),
		[fetchAllEquipmentTypesPerStockLocation, stockLocationId]
	);

	const onBulkEdit = useCallback(() => {
		setFetchingEqLevels(true);
		setEquipmentLevels([]);
		fetchCurrentSLEntities()
			.then((res) => {
				setEquipmentLevels(res);
				setCanOpenEditEquLevel(true);
			})
			.finally(() => setFetchingEqLevels(false));
	}, [fetchCurrentSLEntities]);

	const _onEditEquLevelComplete = () => {
		setEquipmentLevels([]);
		setCanOpenEditEquLevel(false);
	};

	const showUploadPopup = useCallback(() => {
		setEquipmentLevels([]);
		setUploadPopupVisible(true);
	}, []);
	const hideUploadPopup = () => {
		setUploadPopupVisible(false);
	};

	const exportAsCsv = useCallback(
		(data, headers) => {
			const date = moment().format(DATE_FORMAT);
			const csvTitle = `${stockLocation.name}_${date}_equipment_level`;
			const csvExporter = new ExportToCsv({
				...CSV_EXPORT_DEFAULTS,
				filename: csvTitle,
				title: csvTitle,
				headers,
			});

			csvExporter.generateCsv(data);
		},
		[stockLocation.name]
	);

	const onDownloadSample = useCallback(() => {
		setDownloading(true);
		fetchCurrentSLEntities()
			.then((els) => {
				const data =
					els && els.length > 0
						? els.map((_) => getRecordForEquipmentLevel(_))
						: [getRecordForEquipmentLevel()];
				exportAsCsv(data, [
					'Record ID',
					'OW Equipment Type ID',
					'Name',
					'Equipment Type #',
					'On Hand Quantity',
					'Min Qty',
					'Max Qty',
				]);
			})
			.finally(() => setDownloading(false));
	}, [exportAsCsv, fetchCurrentSLEntities]);

	const downloadCSV = useCallback(() => {
		setExportingCsv(true);
		exportCsv({
			...getStateSliceProp('sorting'),
			...getStateSliceProp('filters'),
		}).finally(() => setExportingCsv(false));
	}, [exportCsv, getStateSliceProp]);

	const onDataUpload = useCallback(
		(results) => {
			return new Promise((resolve, reject) => {
				createBulk(
					results.validData.map((_) => ({
						id: _.id ? parseInt(_.id) : undefined,
						stockLocationId: parseInt(stockLocationId),
						supplierFacilityId: parseInt(nullSafeGet('supplierFacilityId', stockLocation)),
						supplierCompanyId: parseInt(nullSafeGet('supplierCompanyId', stockLocation)),
						equipmentTypeId: parseInt(_.equipmentTypeId),
						minQuantity: parseInt(_.minQuantity),
						maxQuantity: parseInt(_.maxQuantity),
					}))
				)
					.then((res) => {
						resolve('Updated successfully!');
						refreshEquipmentTypes();
					})
					.catch(() => {
						reject('Unable to upload! Please contact support!');
					});
			});
		},
		[createBulk, refreshEquipmentTypes, stockLocation, stockLocationId]
	);

	return (
		<>
			<PaginatedReduxTableWithHeader
				targetCollectionName={TC_NAME}
				updateFilters={updateFilters}
				stateSlice={equipmentTypesPerStockLocation}
				clearAndUpdateFilters={clearAndUpdateFilters}
				filterConfig={filterConfig}
				entityCollectionName="equipment_types_per_stock_location"
				tableColumns={columns}
				rowSelection={
					canFieldManageStockLocation(currentUser, stockLocation, companyConfig)
						? rowSelection
						: null
				}
				showHeader
				preAppliedFilters={{ stockLocationId }}
				fetchData={equipmentTypesPerStockLocationRestCrudThunksForSupplier.read}
				hasDefaultHeaderPage
				initialSorters={{
					sort_by: 'modelName',
					order: 'ascend',
				}}
				rightActions={
					canTransfer ? (
						<div>
							<Button onClick={onSelectCancel} size="large">
								Cancel
							</Button>
							<Button className="ml-2" size="large" type="primary" onClick={onTransferClick}>
								Transfer
							</Button>
						</div>
					) : (
						<div className="flex-start flex flex-row">
							<FeildTechSLManagePermissionChecker>
								<AccessPermissionChecker
									name={PERMISSION_NAMES.ALLOW_BULK_OPERATIONS_WHILE_MANAGING_STOCK_LOCATION}
								>
									<Popover content="Download or Upload Equipment Level" trigger="hover">
										<Button
											size="large"
											className="inline-block-visible-md"
											icon={<UploadOutlined />}
											onClick={showUploadPopup}
										>
											<span className="inline-block-visible-xxl">Upload</span>
										</Button>
									</Popover>
								</AccessPermissionChecker>
								<Popover content="Export CSV" trigger="hover">
									<Button
										size="large"
										icon={<DownloadOutlined />}
										className="inline-block-visible-md ml-2"
										onClick={downloadCSV}
										loading={exportingCsv}
									>
										<span className="inline-block-visible-xxl">Export CSV</span>
									</Button>
								</Popover>
							</FeildTechSLManagePermissionChecker>
							<FeildTechSLManagePermissionChecker>
								<AccessPermissionChecker
									name={PERMISSION_NAMES.ALLOW_BULK_OPERATIONS_WHILE_MANAGING_STOCK_LOCATION}
								>
									<Popover
										content={
											editAllowed ? 'Edit Equipment Level' : 'No Equipment available to edit'
										}
										trigger="hover"
									>
										<Button
											size="large"
											className="inline-block-visible-md ml-2"
											loading={fetchingEqLevels}
											icon={<EditOutlined />}
											onClick={onBulkEdit}
											disabled={!editAllowed}
										>
											Edit
										</Button>
									</Popover>
								</AccessPermissionChecker>
							</FeildTechSLManagePermissionChecker>
							<FeildTechSLManagePermissionChecker>
								<Popover content="Add Equipment" trigger="hover">
									<Button
										size="large"
										className="inline-block-visible-md ml-2"
										icon={<PlusOutlined />}
										type="primary"
										onClick={onAddEquipment}
									>
										Add
									</Button>
								</Popover>
							</FeildTechSLManagePermissionChecker>
						</div>
					)
				}
				emptyState={
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/sunset.svg"
								alt="No Equipment Level to display"
							/>
						}
						headline={'No Equipment Level to display.'}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									You haven't added any equipment to this location yet.
								</div>
							</div>
						}
					/>
				}
			/>
			{epslModalVisible && (
				<AddOrEditEquipmentLevelModal
					onCancel={_hideEditModal}
					onSuccess={onEquipmentLevelSuccess}
					stockLocationId={stockLocationId}
					equipmentPerStockLocationLevel={editingRecord}
				/>
			)}
			{transferModalVisible && (
				<BulkTransferEquipmentTypeModal
					onCancel={hideTransferModal}
					stockLocationId={stockLocationId}
					selectedEquipmentTypes={selectedRecords}
					onSuccess={onTransferEquipmentsSuccess}
				/>
			)}
			{canOpenEditEquLevel ? (
				<OWImporter
					dataLoadComplete={canOpenEditEquLevel}
					type="equipment_level"
					fields={EQUIPMENT_IMPORT_HEADER}
					data={equipmentLevels.map((_) => getRecordForEquipmentLevel(_))}
					onComplete={_onEditEquLevelComplete}
					onDataLoad={onDataUpload}
				/>
			) : null}
			{uploadPopupVisible && (
				<ImportGridModal
					visible
					entityName="Equipment Level"
					title="Upload Equipment Level"
					sampleModalText="Do you want to download the existing equipment level?"
					sampleButtonText="Download Equipment Level"
					onCancel={hideUploadPopup}
					onDownloadSample={onDownloadSample}
					downloadingSample={downloading}
					fields={EQUIPMENT_IMPORT_HEADER}
					onDataLoad={onDataUpload}
					showWarningMessage
					onComplete={hideUploadPopup}
				/>
			)}
		</>
	);
};

const mapStateToProps = (state, ownProps) => ({
	equipmentTypesPerStockLocation: state.equipment_types_per_stock_location,
	currentUser: state.session.currentUser,
	stockLocation: getEntityById(ownProps.match.params.id, state.stock_locations),
	companyConfig: state.company_config.detail,
	assetTypes: state.asset_types,
	equipmentTypes: state.equipment_types,
});

const mapDispatchToProps = (dispatch) => ({
	fetchMultipleAssetTypes: (ids, targetCollectionName) =>
		dispatch(assetTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchAssetTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			assetTypesRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleEquipments: (ids, targetCollectionName) =>
		dispatch(equipmentTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchEquipmentTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			equipmentTypesRestCrudThunksForSupplier.read(
				{ ...(params || {}), isActive: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchEquipmentTypesPerStockLocation: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			equipmentTypesPerStockLocationRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchAllEquipmentTypesPerStockLocation: (params) =>
		dispatch(
			fetchAllEquipmentTypesPerStockLocactionForSupplier(params, {
				sort_by: 'modelName',
				order: 'ascend',
			})
		),
	updateFilters: (filters, targetCollection) =>
		dispatch(
			EQUIPMENT_TYPES_PER_STOCK_LOCATION_CRUD_ACTION_CREATORS.updateFilters(
				filters,
				targetCollection
			)
		),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			EQUIPMENT_TYPES_PER_STOCK_LOCATION_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
				filters,
				targetCollectionName
			)
		),
	createBulk: (entities) =>
		dispatch(equipmentTypesPerStockLocationRestCrudThunksForSupplier.createBulk(entities)),
	deleteEquipmentLevel: (entity) =>
		dispatch(equipmentTypesPerStockLocationRestCrudThunksForSupplier.deleteImmediate(entity)),
	exportCsv: (params) =>
		dispatch(equipmentTypesPerStockLocationCsvReportGenerator(ROLE_TYPES.SUPPLIER)(params)),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(StockLocationsEquipmentLevelPage)
);
